import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { useDevTools } from '@/contexts/DevToolsContext';
import { supabase } from '@/integrations/supabase/client';
import { DEV_AUTH_CONFIG } from '@/config/devAuth';
import {
  getDevExtractionCacheSummary,
  clearDevExtractionCache,
} from '@/lib/devExtractionCache';
import {
  STORAGE_KEYS,
  getStorageItem,
  setStorageItem,
  removeStorageItem,
} from '@/lib/storage';
import { Wrench, X, ExternalLink, FileSearch, RefreshCw, Zap, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

// Storage key for runtime skip extraction toggle
const SKIP_EXTRACTION_KEY = STORAGE_KEYS.DEV_SKIP_EXTRACTION;

/**
 * Developer Tools Widget
 * 
 * Only visible in development mode.
 * Provides quick access to states, pages, and debug functions.
 */
export const DevTools = () => {
  const { isOpen, toggleOpen, setIsOpen } = useDevTools();
  const [sessionId, setSessionId] = useState('dev_sess_' + Math.random().toString(36).substring(7));
  const [isOnboardingComplete, setIsOnboardingComplete] = useState<boolean | null>(null);
  const [isTogglingOnboarding, setIsTogglingOnboarding] = useState(false);
  const navigate = useNavigate();
  const { session, user } = useAuth();

  // Skip extraction state (persisted to storage for runtime toggle)
  const [skipExtraction, setSkipExtraction] = useState<boolean>(() => {
    // Initialize from env var or storage
    if (DEV_AUTH_CONFIG.skipExtraction) return true;
    return getStorageItem<string>(SKIP_EXTRACTION_KEY) === 'true';
  });
  const [extractionCacheSummary, setExtractionCacheSummary] = useState(getDevExtractionCacheSummary);

  // Refresh extraction cache summary when panel opens
  useEffect(() => {
    if (isOpen) {
      setExtractionCacheSummary(getDevExtractionCacheSummary());
    }
  }, [isOpen]);

  // Toggle skip extraction and persist to storage
  const toggleSkipExtraction = (enabled: boolean) => {
    setSkipExtraction(enabled);
    try {
      if (enabled) {
        setStorageItem(SKIP_EXTRACTION_KEY, 'true');
        toast.success('Extraction skip enabled - onboarding will use cached/mock data');
      } else {
        removeStorageItem(SKIP_EXTRACTION_KEY);
        toast.info('Extraction skip disabled - onboarding will use Railway API');
      }
    } catch (error) {
      console.error('Failed to persist skip extraction setting:', error);
    }
  };

  // Clear the dev extraction cache
  const handleClearExtractionCache = () => {
    clearDevExtractionCache();
    setExtractionCacheSummary(getDevExtractionCacheSummary());
    toast.success('Extraction cache cleared');
  };

  // Fetch current onboarding status when user changes or panel opens
  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      if (!user?.id || !isOpen) return;

      try {
        // Use RPC function (direct table access blocked by RLS)
        const { data, error } = await supabase.rpc('check_user_onboarding_status', {
          user_id: user.id
        });

        if (error) {
          console.error('Failed to fetch onboarding status:', error);
          setIsOnboardingComplete(false); // Default to false on error
          return;
        }

        setIsOnboardingComplete(data ?? false);
      } catch (err) {
        console.error('Error fetching onboarding status:', err);
        setIsOnboardingComplete(false);
      }
    };

    fetchOnboardingStatus();
  }, [user?.id, isOpen]);

  // Toggle onboarding complete flag
  const toggleOnboardingComplete = async () => {
    if (!user?.id || isOnboardingComplete === null) return;

    setIsTogglingOnboarding(true);
    const newValue = !isOnboardingComplete;

    try {
      if (newValue) {
        // Use RPC to set complete (this is what the app uses)
        const { error } = await supabase.rpc('set_user_onboarding_complete', {
          user_id: user.id
        });

        if (error) {
          toast.error(`Failed to mark onboarding complete: ${error.message}`);
          return;
        }
      } else {
        // Use RPC to reset onboarding status
        const { error } = await supabase.rpc('reset_user_onboarding', {
          user_id: user.id
        });

        if (error) {
          toast.error(`Failed to reset onboarding: ${error.message}`);
          return;
        }
      }

      setIsOnboardingComplete(newValue);
      toast.success(`Onboarding ${newValue ? 'marked complete' : 'reset to incomplete'} - refresh to see changes`);
    } catch (err) {
      toast.error('Error updating onboarding status');
      console.error(err);
    } finally {
      setIsTogglingOnboarding(false);
    }
  };

  // Only render in development
  if (!import.meta.env.DEV) return null;

  const simulateSuccessPage = () => {
    navigate(`/tokens/purchase/success?session_id=${sessionId}`);
    setIsOpen(false);
    toast.info('Navigated to Purchase Success Page simulation');
  };

  const copyToken = () => {
    if (session?.access_token) {
      navigator.clipboard.writeText(session.access_token);
      toast.success('JWT Access Token copied!');
    } else {
      toast.error('No active session token');
    }
  };

  return (
    <div className="fixed bottom-4 left-4 z-50 font-sans">
      {/* Floating Button - Only show when NOT logged in (Toolbar button used when logged in) */}
      {!isOpen && !user && (
        <Button 
          onClick={toggleOpen}
          variant="destructive"
          size="icon"
          className="h-10 w-10 opacity-50 hover:opacity-100 shadow-lg"
          title="Open Developer Tools"
        >
          <Wrench className="h-4 w-4" />
        </Button>
      )}

      {/* Dev Tools Panel */}
      {isOpen && (
        <Card className="w-80 shadow-2xl border-orange-500 border-2 animate-in slide-in-from-bottom-5">
          <CardHeader className="py-3 px-4 bg-orange-50 dark:bg-orange-950/20 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-bold text-orange-700 dark:text-orange-400 flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Dev Tools
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0" 
              onClick={toggleOpen}
            >
              <X className="h-5 w-5" />
            </Button>
          </CardHeader>
          <CardContent className="p-3 space-y-3 max-h-[80vh] overflow-y-auto">
            
            {/* Unified Environment Info */}
            {(() => {
              const url = import.meta.env.VITE_SUPABASE_URL || '';
              const isProdUrl = url.includes('hvpjkfvrqijjjqxgicso');
              const isStagingUrl = url.includes('hrqusulspzgtgbycxjrf');
              
              const borderColor = isProdUrl ? 'border-red-500' : isStagingUrl ? 'border-green-500' : 'border-yellow-500';
              const indicatorColor = isProdUrl ? 'bg-red-100 text-red-800' : isStagingUrl ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';

              return (
                <div className={`bg-muted/30 border border-border/60 border-l-4 rounded-r-md p-2.5 shadow-sm text-xs space-y-1.5 ${borderColor}`}>
                  
                  {/* Row 1: DB Status */}
                  <div className="flex items-center justify-between">
                    <span className="font-bold opacity-70">TARGET DB</span>
                    <span className={`font-bold px-1.5 py-0.5 rounded ${indicatorColor}`}>
                      {isStagingUrl ? 'STAGING' : isProdUrl ? 'PRODUCTION' : 'UNKNOWN'}
                    </span>
                  </div>

                  {/* Row 2: URL (Collapsible/Tiny) */}
                  <div className="font-mono text-[10px] opacity-50 break-all select-all leading-tight">
                    {url || 'No URL Configured'}
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-border/50 my-1" />

                  {/* Row 3: Env Stats */}
                  <div className="grid grid-cols-2 gap-y-1 text-muted-foreground">
                    <div>Mode:</div>
                    <div className="font-medium text-foreground">{import.meta.env.MODE}</div>
                    
                    <div>Bypass:</div>
                    <div className={import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' ? 'text-green-600 font-bold' : 'text-gray-500'}>
                      {import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' ? 'Enabled' : 'Disabled'}
                    </div>

                    <div>Dev User:</div>
                    <div className="font-mono text-[10px] truncate pr-1" title={import.meta.env.VITE_DEV_USER_EMAIL}>
                      {import.meta.env.VITE_DEV_USER_EMAIL || '-'}
                    </div>
                  </div>
                </div>
              );
            })()}
            
            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Auth Status */}
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase text-muted-foreground font-bold tracking-wider">Current Session</Label>
              <div className="text-xs bg-muted p-1.5 rounded break-all font-mono border">
                {user ? user.email : 'Not logged in'}
              </div>
              {user && (
                <Button variant="outline" size="sm" onClick={copyToken} className="w-full text-xs h-7">
                  Copy JWT Token
                </Button>
              )}
            </div>

            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Onboarding Toggle */}
            {user && (
              <>
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase text-muted-foreground font-bold tracking-wider">User Flags</Label>
                  <div className="flex items-center justify-between bg-muted/50 p-2 rounded border">
                    <div className="flex flex-col">
                      <span className="text-xs font-medium">Onboarding Complete</span>
                      <span className="text-[10px] text-muted-foreground">
                        {isOnboardingComplete === null ? 'Loading...' : isOnboardingComplete ? 'User has completed onboarding' : 'Will show onboarding wizard'}
                      </span>
                    </div>
                    <Switch
                      checked={isOnboardingComplete ?? false}
                      onCheckedChange={toggleOnboardingComplete}
                      disabled={isOnboardingComplete === null || isTogglingOnboarding}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs h-7 text-muted-foreground"
                    onClick={() => window.location.reload()}
                  >
                    <RefreshCw className="h-3 w-3 mr-1.5" />
                    Reload to apply
                  </Button>
                </div>

                <hr className="border-gray-100 dark:border-gray-800" />
              </>
            )}

            {/* Skip Extraction Toggle */}
            <div className="space-y-2">
              <Label className="text-[10px] uppercase text-muted-foreground font-bold tracking-wider">Onboarding Speed</Label>
              <div className="flex items-center justify-between bg-muted/50 p-2 rounded border">
                <div className="flex flex-col">
                  <span className="text-xs font-medium flex items-center gap-1.5">
                    <Zap className="h-3 w-3 text-yellow-500" />
                    Skip Extraction
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {skipExtraction
                      ? extractionCacheSummary.exists
                        ? `Using cached: ${extractionCacheSummary.templateName}`
                        : 'Will use mock data'
                      : 'Uses Railway API (slow)'}
                  </span>
                </div>
                <Switch
                  checked={skipExtraction}
                  onCheckedChange={toggleSkipExtraction}
                  disabled={DEV_AUTH_CONFIG.skipExtraction}
                />
              </div>

              {/* Cache info & controls */}
              {extractionCacheSummary.exists && (
                <div className="text-[10px] bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded p-2 space-y-1">
                  <div className="flex justify-between text-green-700 dark:text-green-400">
                    <span>Cached extraction:</span>
                    <span className="font-medium">{extractionCacheSummary.age}</span>
                  </div>
                  <div className="flex justify-between text-green-600 dark:text-green-500">
                    <span>{extractionCacheSummary.fieldsCount} fields</span>
                    <span>{extractionCacheSummary.milestonesCount} milestones</span>
                  </div>
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs h-7 text-muted-foreground"
                onClick={handleClearExtractionCache}
                disabled={!extractionCacheSummary.exists}
              >
                <Trash2 className="h-3 w-3 mr-1.5" />
                Clear extraction cache
              </Button>

              {DEV_AUTH_CONFIG.skipExtraction && (
                <p className="text-[9px] text-amber-600 dark:text-amber-400 px-1">
                  Locked by VITE_DEV_SKIP_EXTRACTION env var
                </p>
              )}
            </div>

            <hr className="border-gray-100 dark:border-gray-800" />

            {/* Quick Navigation */}
            <div className="space-y-2">
              <Label className="text-[10px] uppercase text-muted-foreground font-bold tracking-wider">Simulate</Label>
              
              <div className="flex gap-1.5">
                <Input 
                  value={sessionId} 
                  onChange={(e) => setSessionId(e.target.value)} 
                  className="h-7 text-xs font-mono"
                  placeholder="Session ID"
                />
                <Button size="sm" onClick={simulateSuccessPage} className="h-7 px-2.5 bg-green-600 hover:bg-green-700 text-white" title="Simulate Purchase Success">
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7 px-2 justify-start w-full border-orange-400 text-orange-600 hover:bg-orange-950/20 hover:text-orange-500"
                onClick={() => { navigate('/dev/review'); setIsOpen(false); }}
              >
                <FileSearch className="h-3 w-3 mr-1.5" />
                Transaction Review (Bypass)
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7 px-2 justify-start w-full border-purple-400 text-purple-600 hover:bg-purple-950/20 hover:text-purple-500"
                onClick={() => { navigate('/dev/transaction'); setIsOpen(false); }}
              >
                <Zap className="h-3 w-3 mr-1.5" />
                Mock Transaction Overview
              </Button>
            </div>

          </CardContent>
        </Card>
      )}
    </div>
  );
};
